import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { resolveProTenantSubdomain } from './pro-tenant';

// Structural minimum of a TanStack Query, typed locally so this file doesn't
// pin a specific @tanstack/query-core version (the persist-client and
// react-query can resolve to slightly different core versions, whose nominal
// `Query` types aren't mutually assignable).
type PersistableQuery = {
  queryKey: readonly unknown[];
  state: { status: string };
};

// ─────────────────────────────────────────────────────────────────────
// Offline-first query cache for the pro portal.
//
// Motivation: the catalogue (articles + pricing + published categories) is
// the heavy fetch that gated the whole home page behind a skeleton on every
// cold load / hard reload. Persisting it to IndexedDB lets the very first
// render hydrate from disk — products appear instantly, then refresh in the
// background.
//
// We persist ONLY the tenant-shared catalogue queries, never user-scoped
// data (favourites, wallet balance, notifications, documents). That keeps
// sensitive/per-user state off disk entirely and means the persistence
// buster only has to be scoped to the TENANT, sidestepping the cross-account
// cache-bleed class of bug: the persisted blob simply never contains data
// that could bleed between two pro users of the same tenant (they share the
// same catalogue) — and a different tenant is a different hostname, hence a
// different buster.
// ─────────────────────────────────────────────────────────────────────

export const PRO_QUERY_CACHE_KEY = 'tunelec-pro.query-cache.v1';

// The query-key roots that are safe (and worth) persisting. All are
// tenant-level, read-mostly catalogue data. Anything not listed here stays
// in-memory only.
const PERSISTED_QUERY_ROOTS = new Set<string>([
  'pro-articles',
  'pro-article-pricing',
  'pro-published-categories',
  'pro-article-categories',
  'pro-article-families',
  'pro-settings',
  // Le catalogue partagé décide si la bascule Boutique/Catalogue s'affiche :
  // persisté, la décision est immédiate au rechargement au lieu d'apparaître
  // après coup.
  'pro-catalog',
]);

const idbStorage = {
  getItem: (key: string) => idbGet<string>(key).then((v) => v ?? null),
  setItem: (key: string, value: string) => idbSet(key, value),
  removeItem: (key: string) => idbDel(key),
};

export const proQueryPersister = createAsyncStoragePersister({
  storage: idbStorage,
  key: PRO_QUERY_CACHE_KEY,
  throttleTime: 1500,
});

/**
 * Tenant-scoped buster. Derived synchronously at boot from the hostname (or
 * the env fallback), so it's known before the first cache restore. A blob
 * stamped with a different tenant is discarded on restore rather than
 * hydrated. Team members of the same tenant share the catalogue cache.
 */
export function proPersistedCacheBuster(): string {
  const tenant = resolveProTenantSubdomain();
  return tenant ? `tenant:${tenant}` : 'anon';
}

/** Only dehydrate successful, catalogue-scoped queries to disk. */
export function shouldPersistProQuery(query: PersistableQuery): boolean {
  if (query.state.status !== 'success') return false;
  const root = Array.isArray(query.queryKey) ? query.queryKey[0] : undefined;
  return typeof root === 'string' && PERSISTED_QUERY_ROOTS.has(root);
}
